/**
 * Pure helper that maps recent MIDI events to an answer-option ID for
 * the current exercise. Extracted from Practice.tsx so it can be unit-
 * tested without mounting the page or instantiating MidiInput, mirroring
 * how `vocal-grading.ts` was extracted in C.1 and `lesson-completion.ts`
 * in B.3.
 *
 * Phase 2A C.2 supports four exercise types directly:
 *   scale-degree-recognition   (single noteon, octave-agnostic)
 *   interval-recognition       (two consecutive noteons, order-insensitive)
 *   chord-quality-recognition  (3-4 simultaneous noteons; triads + 7ths)
 *   chord-inversion-recognition (same; bass disambiguates the inversion)
 *
 * Out of scope (per spec §10.3):
 *   progression / cadence / mode / secondary-dominant / modulation —
 *   these don't fit MIDI input cleanly. The dispatcher returns an
 *   "unsupported" status so Practice can show the "switch input mode"
 *   hint near the options without trying to grade.
 *
 * Slash chord and extended/altered chord-quality grading also return
 * unsupported in C.2: `identifyChord` only matches triads + 7ths, so
 * any time the played chord-set fits an unsupported pattern, the
 * grader silently no-ops rather than guessing.
 */

import type { Exercise } from "@music-app/exercise-generator";
import {
  gradeMidiChordInversion,
  gradeMidiChordQuality,
  gradeMidiInterval,
  gradeMidiScaleDegree,
  type MidiNoteWithTimestamp,
} from "@music-app/input-midi";

export type MidiSupportedExerciseType =
  | "scale-degree-recognition"
  | "interval-recognition"
  | "chord-quality-recognition"
  | "chord-inversion-recognition"
  | "melodic-dictation";

/**
 * Exercise types where MIDI input does *something* — either auto-grades
 * the answer (recognition exercises) or feeds the in-progress dictation
 * buffer (melodic-dictation). Practice's MidiInput subscription effect
 * uses this gate to decide whether to spin up the runtime; the
 * `gradeMidiAnswer` dispatcher below has its own per-type behavior.
 *
 * `melodic-dictation` belongs here even though `gradeMidiAnswer` returns
 * null for it: the noteon stream is still relevant — Practice forks
 * dictation noteons to `<DictationInput>`'s `onMidiNoteOn` rather than
 * auto-submitting.
 */
export const MIDI_SUPPORTED_EXERCISE_TYPES: ReadonlySet<string> = new Set<string>([
  "scale-degree-recognition",
  "interval-recognition",
  "chord-quality-recognition",
  "chord-inversion-recognition",
  "melodic-dictation",
]);

export function isMidiSupportedExercise(
  exerciseType: string,
): exerciseType is MidiSupportedExerciseType {
  return MIDI_SUPPORTED_EXERCISE_TYPES.has(exerciseType);
}

export interface MidiGradingResult {
  optionId: string;
}

/**
 * Decide whether a stream of recent MIDI noteons should auto-submit an
 * answer. Returns null when:
 *   - The exercise type isn't MIDI-supported (callers should NOT call
 *     this for unsupported types in the first place — gate at the
 *     subscription site — but we no-op defensively).
 *   - There aren't enough notes for the type (e.g. only one noteon for
 *     an interval exercise).
 *   - The played notes don't map cleanly to an option (e.g. a chromatic
 *     pitch in a diatonic-only scale-degree exercise; an extended chord
 *     in chord-quality).
 *
 * The caller (Practice) is responsible for windowing the noteon buffer
 * — this helper just inspects what it's given. For interval, the helper
 * uses the last two noteons; for chord exercises, it uses the entire
 * buffer (the underlying graders apply the simultaneity-window filter).
 */
export function gradeMidiAnswer(
  noteons: ReadonlyArray<MidiNoteWithTimestamp>,
  exercise: Exercise,
): MidiGradingResult | null {
  if (noteons.length === 0) return null;
  switch (exercise.exerciseType) {
    case "scale-degree-recognition": {
      // Only the most recent note matters — the user can repress to
      // change their answer before the chime fires.
      const latest = noteons[noteons.length - 1]!;
      const result = gradeMidiScaleDegree(latest.midi, exercise);
      return result ? { optionId: result.optionId } : null;
    }
    case "interval-recognition": {
      if (noteons.length < 2) return null;
      const a = noteons[noteons.length - 2]!;
      const b = noteons[noteons.length - 1]!;
      const result = gradeMidiInterval(a.midi, b.midi, exercise);
      return result ? { optionId: result.optionId } : null;
    }
    case "chord-quality-recognition": {
      const result = gradeMidiChordQuality(noteons, exercise);
      return result ? { optionId: result.optionId } : null;
    }
    case "chord-inversion-recognition": {
      const result = gradeMidiChordInversion(noteons, exercise);
      return result ? { optionId: result.optionId } : null;
    }
    default:
      return null;
  }
}
